import { HStack, Flex, Checkbox, Box } from '@cad-arc';
import { partition } from 'lodash';
import { UnitStatusSearchOptions } from '@mark43/cad-api';
import { useEffect, useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { UnitStatusSearchFormType } from './types';
import { UNIT_SEARCH_STATUSES_COMMON_WIDTH } from '../search-windows/constants';

export function UnitStatusSearchFormStatusesSection({
    form,
    options,
}: {
    form: UseFormReturn<UnitStatusSearchFormType>;
    options: UnitStatusSearchOptions;
}) {
    const selectedUnitStates = useWatch({ control: form.control, name: 'unitStates' });
    const [selectedEventRelatedUnitStates, selectedNonEventRelatedUnitStates] = useMemo(
        () => partition(selectedUnitStates, (unitStateOption) => unitStateOption.isEventRelated),
        [selectedUnitStates]
    );
    const [allEventRelatedUnitStates, allNonEventRelatedUnitStates] = useMemo(
        () =>
            partition(
                options.unitStateOptions,
                (unitStateOption) => unitStateOption.isEventRelated
            ),
        [options]
    );
    const isSelectingAllEventRelatedUnitStates =
        selectedEventRelatedUnitStates.length === allEventRelatedUnitStates.length;
    const isSelectingAllNonEventRelatedUnitStates =
        selectedNonEventRelatedUnitStates.length === allNonEventRelatedUnitStates.length;

    const showAssignmentType = selectedNonEventRelatedUnitStates.length > 0;
    useEffect(() => {
        if (!showAssignmentType) {
            form.resetField('assignmentTypes');
        }
    }, [showAssignmentType, form]);

    return (
        <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={4}>
            <HStack gap={1}>
                <Checkbox
                    isChecked={isSelectingAllEventRelatedUnitStates}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                            form.setValue('unitStates', [
                                ...allEventRelatedUnitStates,
                                ...selectedUnitStates.filter(
                                    (unitState) => !unitState.isEventRelated
                                ),
                            ]);
                        } else {
                            form.setValue(
                                'unitStates',
                                selectedUnitStates.filter((unitState) => !unitState.isEventRelated)
                            );
                        }
                    }}
                >
                    All Event Related Statuses
                </Checkbox>
                <Checkbox
                    isChecked={isSelectingAllNonEventRelatedUnitStates}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                            form.setValue('unitStates', [
                                ...selectedUnitStates.filter(
                                    (unitState) => unitState.isEventRelated
                                ),
                                ...allNonEventRelatedUnitStates,
                            ]);
                        } else {
                            form.setValue(
                                'unitStates',
                                selectedUnitStates.filter((unitState) => unitState.isEventRelated)
                            );
                        }
                    }}
                >
                    All Non-Event Related Statuses
                </Checkbox>
            </HStack>
            <Flex alignItems="flex-start" flexWrap="wrap" gap={4} width="100%">
                <Box w={UNIT_SEARCH_STATUSES_COMMON_WIDTH}>
                    <FormSelect
                        name="unitStates"
                        label="Statuses"
                        control={form.control}
                        options={options.unitStateOptions}
                        isMultiple
                        truncateSelectedCount={2}
                    />
                </Box>
                {showAssignmentType && (
                    <Box w={UNIT_SEARCH_STATUSES_COMMON_WIDTH}>
                        <FormSelect
                            name="assignmentTypes"
                            label="Assignment Type"
                            control={form.control}
                            options={options.assignmentTypeOptions}
                            isMultiple
                            truncateSelectedCount={2}
                        />
                    </Box>
                )}
            </Flex>
        </Flex>
    );
}
